import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import AgentLayout from './AgentLayout';

const buildEmptyForm = (employerName) => ({
  title: '', description: '', company_name: employerName || '', location: '',
  work_mode: 'office', job_type: 'full_time', work_shift: 'day',
  skills_required: '', salary_min: '', salary_max: '',
  experience_required: '', openings: '', benefits: '',
  contact_number: '',
});

// Builds a reasonable draft description from the other fields already
// filled in the form. Agent can edit the result afterward — this is
// just a starting point, not a final copy.
const generateJobDescription = (data) => {
  const {
    title, company_name, location, work_mode, job_type, work_shift,
    skills_required, experience_required, salary_min, salary_max,
    openings, benefits,
  } = data;

  if (!title) return '';

  const workModeLabel = { office: 'in-office', hybrid: 'hybrid', work_from_home: 'remote' }[work_mode] || work_mode;
  const jobTypeLabel = { full_time: 'a full-time', part_time: 'a part-time', internship: 'an internship', contract: 'a contract' }[job_type] || job_type;
  const shiftLabel = work_shift === 'night' ? 'night shift' : 'day shift';

  let lines = [];

  lines.push(
    `${company_name || 'We'} ${company_name ? 'is' : 'are'} hiring for the position of ${title}${location ? ` in ${location}` : ''}. This is ${jobTypeLabel} role, ${workModeLabel}, working ${shiftLabel}.`
  );

  if (experience_required) {
    lines.push(`We're looking for candidates with around ${experience_required} year${experience_required == 1 ? '' : 's'} of relevant experience.`);
  }
  if (skills_required) {
    lines.push(`Key skills required: ${skills_required}.`);
  }
  if (salary_min || salary_max) {
    const salaryText = salary_min && salary_max
      ? `₹${salary_min} - ₹${salary_max}`
      : `₹${salary_min || salary_max}`;
    lines.push(`Compensation: ${salaryText} per annum, based on experience and fit.`);
  }
  if (openings) {
    lines.push(`We currently have ${openings} opening${openings == 1 ? '' : 's'} for this role.`);
  }
  if (benefits) {
    lines.push(`Benefits include: ${benefits}.`);
  }
  lines.push("If this sounds like a good fit, we'd love to hear from you — apply today!");

  return lines.join('\n\n');
};

function AgentPostJob() {
  const { id } = useParams(); // employer id
  const navigate = useNavigate();

  const [employer, setEmployer] = useState(null);
  const [loadingEmployer, setLoadingEmployer] = useState(true);
  const [formData, setFormData] = useState(buildEmptyForm(''));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // ----- AI: role/title autocomplete -----
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [loadingRole, setLoadingRole] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const roleDebounceRef = useRef(null);

  // ----- AI: skills autocomplete -----
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDebounceRef = useRef(null);

  // ----- AI: location autocomplete -----
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationDebounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/agent/employers/${id}`);
        setEmployer(res.data);
        setFormData(buildEmptyForm(res.data.name));
      } catch (err) {
        console.error('Failed to load employer', err);
      } finally {
        setLoadingEmployer(false);
      }
    })();
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAutoGenerateDescription = () => {
    const generated = generateJobDescription(formData);
    if (!generated) {
      setMessage('Fill in the job title first, then try again.');
      return;
    }
    setFormData((prev) => ({ ...prev, description: generated }));
    setMessage('');
  };

  // ----- Role/Title -----
  const handleTitleChange = (e) => {
    const text = e.target.value;
    setFormData((prev) => ({ ...prev, title: text }));

    if (roleDebounceRef.current) clearTimeout(roleDebounceRef.current);
    if (text.trim().length < 2) {
      setRoleSuggestions([]);
      setShowRoleDropdown(false);
      return;
    }
    roleDebounceRef.current = setTimeout(async () => {
      setLoadingRole(true);
      try {
        const res = await api.post('/ai/suggest-keywords', { query: text });
        setRoleSuggestions(res.data.suggestions || []);
        setShowRoleDropdown(true);
      } catch (err) {
        console.error('Failed to fetch role suggestions', err);
      } finally {
        setLoadingRole(false);
      }
    }, 350);
  };

  const selectRole = (role) => {
    setFormData((prev) => ({ ...prev, title: role }));
    setShowRoleDropdown(false);
    setRoleSuggestions([]);
  };

  // ----- Location -----
  const handleLocationChange = (text) => {
    setFormData((prev) => ({ ...prev, location: text }));

    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    if (text.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }
    locationDebounceRef.current = setTimeout(async () => {
      setLoadingLocation(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: text });
        setLocationSuggestions(res.data.suggestions || []);
        setShowLocationDropdown(true);
      } catch (err) {
        console.error('Failed to fetch location suggestions', err);
      } finally {
        setLoadingLocation(false);
      }
    }, 350);
  };

  const selectLocation = (loc) => {
    setFormData((prev) => ({ ...prev, location: loc }));
    setShowLocationDropdown(false);
    setLocationSuggestions([]);
  };

  // ----- Skills -----
  const handleSkillsChange = (e) => {
    const text = e.target.value;
    setFormData((prev) => ({ ...prev, skills_required: text }));

    const segments = text.split(',');
    const current = segments[segments.length - 1].trim();

    if (skillDebounceRef.current) clearTimeout(skillDebounceRef.current);
    if (current.length < 2) {
      setSkillSuggestions([]);
      setShowSkillDropdown(false);
      return;
    }
    skillDebounceRef.current = setTimeout(async () => {
      setLoadingSkills(true);
      try {
        const existing = segments.slice(0, -1).map((s) => s.trim()).filter(Boolean);
        const res = await api.post('/ai/suggest-keywords', { query: current });
        setSkillSuggestions((res.data.suggestions || []).filter((s) => !existing.includes(s)));
        setShowSkillDropdown(true);
      } catch (err) {
        console.error('Failed to fetch skill suggestions', err);
      } finally {
        setLoadingSkills(false);
      }
    }, 350);
  };

  const selectSkill = (skill) => {
    const segments = formData.skills_required.split(',');
    segments[segments.length - 1] = ` ${skill}`;
    const cleaned = segments.map((s) => s.trim()).filter(Boolean).join(', ');
    setFormData((prev) => ({ ...prev, skills_required: cleaned + ', ' }));
    setShowSkillDropdown(false);
    setSkillSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setMessage('');
    try {
      await api.post(`/agent/employers/${id}/jobs`, formData);
      setMessage('Job posted successfully!');
      setTimeout(() => navigate(`/agent/employers/${id}`), 1000);
    } catch (err) {
      const data = err.response?.data;
      setErrors(data?.errors || {});
      setMessage(data?.message || 'Failed to post job.');
    } finally {
      setSaving(false);
    }
  };

  const isUnverified =
  !loadingEmployer && employer && employer.company_verified_status !== 'verified';

  return (
    <AgentLayout>
      <div className="max-w-4xl mx-auto">
        <Link to={`/agent/employers/${id}`} className="text-sm text-gray-500 hover:text-[#2563EB] inline-flex items-center gap-1">
          ← Back to Employer
        </Link>

        {/* Header card */}
        <div className="bg-gradient-to-r from-[#0D1B3D] to-[#1e3a8a] rounded-2xl px-6 py-6 mt-3 mb-6 shadow-sm">
          <h2 className="text-2xl font-bold text-white">Post a Job</h2>
          <p className="text-blue-200 text-sm mt-1">
            {loadingEmployer ? 'Loading employer…' : `Posting on behalf of ${employer?.name || 'this employer'}`}
          </p>
        </div>

        {message && (
          <div className={`mb-6 text-sm px-4 py-3 rounded-lg border ${
            message.toLowerCase().includes('success')
              ? 'bg-green-50 text-green-700 border-green-100'
              : 'bg-red-50 text-red-700 border-red-100'
          }`}>
            {message.toLowerCase().includes('success') ? '✓ ' : ''}{message}
          </div>
        )}

        {isUnverified ? (
  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-6 text-sm">
    <p className="font-semibold mb-1">This employer is not verified</p>
    <p>Jobs can only be posted for verified employers. Please get the employer verified first.</p>
    <Link
      to={`/agent/employers/${id}`}
      className="inline-block mt-4 text-sm font-semibold text-[#2563EB] hover:underline"
    >
      ← Back to Employer
    </Link>
  </div>
) : (
<form onSubmit={handleSubmit} className="space-y-6">

          {/* Section: Role */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-4 pb-2 border-b border-gray-100">
              💼 Role
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Job Title with AI autocomplete */}
              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Job Title</label>
                <input
                  type="text" name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  onFocus={() => roleSuggestions.length > 0 && setShowRoleDropdown(true)}
                  onBlur={() => setTimeout(() => setShowRoleDropdown(false), 150)}
                  required
                  autoComplete="off"
                  placeholder="e.g. Sales Executive"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                />
                {loadingRole && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showRoleDropdown && roleSuggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {roleSuggestions.map((role, i) => (
                      <button
                        key={i} type="button"
                        onMouseDown={() => selectRole(role)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#EFF6FF]"
                      >
                        💼 {role}
                      </button>
                    ))}
                  </div>
                )}
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title[0]}</p>}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Company Name</label>
                <input
                  type="text" name="company_name"
                  value={formData.company_name} onChange={handleChange} required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                />
              </div>

              {/* Location with AI autocomplete */}
              <div className="relative">
                <label className="text-xs text-gray-400 uppercase mb-1 block">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  onFocus={() => formData.location.length >= 2 && setShowLocationDropdown(true)}
                  onBlur={() => setTimeout(() => setShowLocationDropdown(false), 150)}
                  placeholder="e.g. Bengaluru"
                  autoComplete="off"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                />
                {loadingLocation && (
                  <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
                )}
                {showLocationDropdown && locationSuggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    {locationSuggestions.map((loc, i) => (
                      <button
                        key={i} type="button"
                        onMouseDown={() => selectLocation(loc)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#EFF6FF]"
                      >
                        📍 {loc}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Contact Number</label>
                <input
                  type="tel" name="contact_number"
                  value={formData.contact_number} onChange={handleChange}
                  pattern="[0-9]{10,15}" title="Enter a valid mobile number (digits only)"
                  placeholder="For Call / WhatsApp"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                />
              </div>
            </div>
          </div>

          {/* Section: Job Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-4 pb-2 border-b border-gray-100">
              ⚙️ Job Details
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Work Mode</label>
                <select name="work_mode" value={formData.work_mode} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]">
                  <option value="office">Office</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="work_from_home">Work From Home</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Job Type</label>
                <select name="job_type" value={formData.job_type} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]">
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="internship">Internship</option>
                  <option value="contract">Contract</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Work Shift</label>
                <select name="work_shift" value={formData.work_shift} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]">
                  <option value="day">Day Shift</option>
                  <option value="night">Night Shift</option>
                </select>
              </div>
            </div>

            {/* Skills with AI autocomplete */}
            <div className="relative mb-4">
              <label className="text-xs text-gray-400 uppercase mb-1 block">Skills Required</label>
              <input
                type="text" name="skills_required"
                placeholder="e.g. PHP, Laravel, MySQL"
                value={formData.skills_required}
                onChange={handleSkillsChange}
                onFocus={() => skillSuggestions.length > 0 && setShowSkillDropdown(true)}
                onBlur={() => setTimeout(() => setShowSkillDropdown(false), 150)}
                autoComplete="off"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              />
              {loadingSkills && (
                <span className="absolute right-3 top-9 text-xs text-gray-400">…</span>
              )}
              {showSkillDropdown && skillSuggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  {skillSuggestions.map((skill, i) => (
                    <button
                      key={i} type="button"
                      onMouseDown={() => selectSkill(skill)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#EFF6FF]"
                    >
                      🧩 {skill}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Salary Min</label>
                <input type="number" name="salary_min" value={formData.salary_min} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Salary Max</label>
                <input type="number" name="salary_max" value={formData.salary_max} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Experience Required (yrs)</label>
                <input type="number" name="experience_required" value={formData.experience_required} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">Openings</label>
                <input type="number" name="openings" value={formData.openings} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 uppercase mb-1 block">Benefits</label>
              <textarea name="benefits" value={formData.benefits} onChange={handleChange} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
            </div>
          </div>

                    {/* Section: Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-4 pb-2 border-b border-gray-100">
              📝 Job Description
            </h4>

            {/* Centered auto-generate button */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                onClick={handleAutoGenerateDescription}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] bg-[#EFF6FF] hover:bg-blue-100 border border-blue-200 px-5 py-2.5 rounded-lg transition-colors"
              >
                ✨ Auto-generate from details above
              </button>
            </div>

            <textarea
              name="description"
              value={formData.description} onChange={handleChange} required rows={6}
              placeholder="Job description..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description[0]}</p>}
            <p className="text-xs text-gray-400 mt-1 text-center">
              Fill in the fields above, then click "Auto-generate" for a starting draft — you can edit it freely afterward.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Link
              to={`/agent/employers/${id}`}
              className="text-sm font-medium text-gray-500 hover:text-gray-700 px-5 py-2.5 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit" disabled={saving}
              className="bg-[#0D1B3D] hover:bg-[#14265c] text-white px-6 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              {saving ? 'Posting…' : 'Post Job'}
            </button>
          </div>
        </form>
)}
      </div>
    </AgentLayout>
  );
}

export default AgentPostJob;